import React, { useState, useEffect, useRef } from 'react';
import { useMessages, useConversations } from '../hooks/useMessages';
import { useAuth } from './AuthContext';
import { IoChatbubbleEllipses, IoSend, IoCheckmark } from 'react-icons/io5';

const MessagesManager = () => {
  const { currentUser } = useAuth();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { conversations, loading: conversationsLoading, error: conversationError } = useConversations(
    currentUser?.uid,
    true // isAdmin
  );
  
  const { messages, loading: messagesLoading, sendMessage, markMessagesAsRead, error: messageError } = useMessages(
    selectedConversationId || undefined
  );

  // Auto-scroll messages container to bottom
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-select first conversation when conversations load
  useEffect(() => {
    if (conversations.length > 0 && !selectedConversationId) {
      setSelectedConversationId(conversations[0].id || null);
    }
  }, [conversations, selectedConversationId]);

  // Mark messages as read when conversation is selected
  useEffect(() => {
    if (selectedConversationId && currentUser?.uid) {
      markMessagesAsRead(selectedConversationId, currentUser.uid, true);
    }
  }, [selectedConversationId, currentUser?.uid, markMessagesAsRead]);

  const selectedConversation = conversations.find(c => c.id === selectedConversationId);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageText.trim() || !selectedConversationId || !currentUser) return;

    setSending(true);
    try {
      await sendMessage(
        messageText,
        selectedConversationId,
        currentUser.uid,
        currentUser.displayName || 'Admin',
        currentUser.email || 'admin@jkcc.com',
        true // isAdmin
      );
      setMessageText('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (conversationsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-zinc-800"></div>
      </div>
    );
  }

  if (conversationError) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center h-96 flex items-center justify-center">
        <div>
          <p className="text-red-600 font-semibold">Error loading conversations</p>
          <p className="text-gray-600 text-sm mt-2">{conversationError}</p>
          {conversationError.includes('index') && (
            <p className="text-yellow-600 text-xs mt-3 font-semibold">
              Check browser console for link to create Firestore index
            </p>
          )}
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center h-96 flex items-center justify-center">
        <div>
          <IoChatbubbleEllipses size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 text-lg">No customer inquiries yet</p>
          <p className="text-gray-500 text-sm mt-2">Messages from customers will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex h-[600px]">
      {/* Conversations List */}
      <div className="w-80 border-r border-gray-200 bg-gray-50 overflow-y-auto">
        {conversations.map((conversation) => (
          <button
            key={conversation.id}
            onClick={() => {
              setSelectedConversationId(conversation.id || null);
              setMessageText('');
            }}
            className={`w-full text-left p-4 border-b border-gray-200 transition-all ${
              selectedConversationId === conversation.id
                ? 'bg-yellow-50 border-l-4 border-l-zinc-800'
                : 'hover:bg-gray-100'
            } ${(conversation.adminUnreadCount || 0) > 0 ? 'bg-red-50' : ''}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className={`font-semibold truncate ${(conversation.adminUnreadCount || 0) > 0 ? 'text-red-700' : 'text-gray-800'}`}>
                  {conversation.userName}
                </h4>
                <p className="text-xs text-gray-500 truncate">{conversation.userEmail}</p>
              </div>
              {(conversation.adminUnreadCount || 0) > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {conversation.adminUnreadCount}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-2 truncate">
              {conversation.lastMessage || 'No messages yet'}
            </p>
            {conversation.lastMessageTime && (
              <p className="text-xs text-gray-400 mt-1">
                {conversation.lastMessageTime.toDate?.().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
          </button>
        ))}
      </div>

      {/* Messages Panel */}
      {selectedConversation ? (
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="border-b border-gray-200 p-4 bg-gray-50">
            <h3 className="font-bold text-lg text-gray-800">{selectedConversation.userName}</h3>
            <p className="text-sm text-gray-600">{selectedConversation.userEmail}</p>
          </div>

          {/* Messages Container */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-white">
            {messageError ? (
              <div className="flex items-center justify-center h-full text-red-500">
                <div className="text-center">
                  <p className="font-semibold">Error loading messages</p>
                  <p className="text-sm mt-2">{messageError}</p>
                  {messageError.includes('index') && (
                    <p className="text-xs text-yellow-600 mt-3">
                      Check browser console for link to create Firestore index
                    </p>
                  )}
                </div>
              </div>
            ) : messagesLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-zinc-800"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                <p>No messages in this conversation</p>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div key={msg.id}>
                    {/* Product Info Card */}
                    {msg.productId && (
                      <div className="mb-3 flex justify-start">
                        <div className="max-w-xs bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                          {msg.productImage && (
                            <img 
                              src={msg.productImage} 
                              alt={msg.productName}
                              className="w-full h-32 object-cover"
                            />
                          )}
                          <div className="p-3">
                            <p className="text-xs font-semibold text-gray-600">Asking about:</p>
                            <p className="font-bold text-zinc-800 text-sm">{msg.productName}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Message Bubble */}
                    <div
                      className={`flex ${msg.isAdmin ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs px-4 py-2 rounded-lg text-sm ${
                          msg.isAdmin
                            ? 'bg-zinc-800 text-white'
                            : 'bg-gray-100 border border-gray-200 text-gray-800'
                        }`}
                      >
                        <p className="font-semibold text-xs mb-1 opacity-75">
                          {msg.senderName}
                        </p>
                        <p className="break-words">{msg.message}</p>
                        <div className="flex items-center gap-1 mt-1 text-xs opacity-70">
                          <span>
                            {msg.createdAt?.toDate?.().toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                          {msg.isAdmin && <IoCheckmark size={14} />}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex gap-2">
              <input
                type="text"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type your reply..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-transparent outline-none text-sm"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !messageText.trim()}
                className="bg-zinc-800 text-white px-4 py-2 rounded-lg hover:bg-zinc-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <IoSend size={16} />
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <p>Select a conversation to view messages</p>
        </div>
      )}
    </div>
  );
};

export default MessagesManager;
